import React, { useState, useEffect, useContext } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, addDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ToastContext } from '@/components/ToastProvider';
import { DollarSign, CheckCircle, XCircle, Clock, RefreshCw, Filter, Search } from 'lucide-react';
import { formatPeso } from '@/lib/format';

export default function CashOutManagement() {
  const { notify } = useContext(ToastContext);
  const [loading, setLoading] = useState(true);
  const [cashOutRequests, setCashOutRequests] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState(null);

  // Load cash out requests
  useEffect(() => {
    const q = query(
      collection(db, 'store_cash_out_requests'),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        requestedAt: doc.data().requestedAt?.toDate()
      }));
      setCashOutRequests(requests);
      setLoading(false);
    }, (error) => {
      notify('Error loading cash out requests', 'error');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [notify]);

  const handleApproveRequest = async (requestId) => {
    try {
      setProcessing(requestId);
      
      // Update request status
      await updateDoc(doc(db, 'store_cash_out_requests', requestId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: 'treasurer' // This would be the actual user ID in real implementation
      });

      // Create transaction record for the actual cash out
      await addDoc(collection(db, 'storeWalletTransactions'), {
        storeId: cashOutRequests.find(r => r.id === requestId)?.storeId,
        type: 'debit',
        amount: cashOutRequests.find(r => r.id === requestId)?.amount,
        description: `Cash out approved - ${cashOutRequests.find(r => r.id === requestId)?.reason || 'No reason provided'}`,
        balanceAfter: 0, // This would be calculated based on current balance
        createdAt: serverTimestamp(),
        processedBy: 'treasurer',
        transactionType: 'cash_out_approved'
      });

      notify('Cash out request approved successfully', 'success');
    } catch (error) {
      notify('Error approving request', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      setProcessing(requestId);
      
      await updateDoc(doc(db, 'store_cash_out_requests', requestId), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: 'treasurer',
        rejectionReason: reason
      });

      // Refund the amount back to wallet
      const request = cashOutRequests.find(r => r.id === requestId);
      if (request) {
        // Get current wallet balance
        const storeDoc = await getDoc(doc(db, 'stores', request.storeId));
        const currentBalance = storeDoc.data()?.walletBalance || 0;
        const newBalance = currentBalance + request.amount;

        // Update store wallet balance
        await updateDoc(doc(db, 'stores', request.storeId), {
          walletBalance: newBalance,
          lastUpdated: serverTimestamp()
        });

        // Create refund transaction
        await addDoc(collection(db, 'storeWalletTransactions'), {
          storeId: request.storeId,
          type: 'credit',
          amount: request.amount,
          description: `Cash out request rejected - Refund: ${reason || 'No reason provided'}`,
          balanceAfter: newBalance,
          createdAt: serverTimestamp(),
          processedBy: 'system',
          transactionType: 'cash_out_refund'
        });
      }

      notify('Cash out request rejected and amount refunded', 'success');
    } catch (error) {
      notify('Error rejecting request', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'approved':
        return 'text-green-600 bg-green-50';
      case 'rejected':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  // Filter requests
  const filteredRequests = cashOutRequests.filter(request => {
    const matchesFilter = filter === 'all' || request.status === filter;
    const matchesSearch = searchTerm === '' || 
      request.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading cash out requests...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <DollarSign className="w-8 h-8 mr-3 text-green-600" />
            Cash Out Management
          </h1>
          <p className="text-gray-600 mt-1">Manage store cash out requests and approvals</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Status:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input text-sm"
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-64">
            <Search className="w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by store name or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input text-sm flex-1"
            />
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Cash Out Requests ({filteredRequests.length})
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredRequests.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No cash out requests found.
            </div>
          ) : (
            filteredRequests.map((request) => (
              <div key={request.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {request.storeName}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {request.storeType === 'cooperative' ? 'Cooperative Store' : 
                         request.storeType === 'member' ? 'Member Store' : 'Establishment Store'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Requested: {request.requestedAt?.toLocaleString() || 'Unknown date'}
                      </p>
                      {request.reason && (
                        <p className="text-sm text-gray-600 mt-1">
                          Reason: {request.reason}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900">
                      {formatPeso(request.amount)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproveRequest(request.id)}
                            disabled={processing === request.id}
                            className="btn btn-success btn-sm flex items-center"
                          >
                            {processing === request.id ? (
                              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) handleRejectRequest(request.id, reason);
                            }}
                            disabled={processing === request.id}
                            className="btn btn-danger btn-sm flex items-center"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </>
                      )}
                      {request.status === 'approved' && (
                        <span className="text-sm text-green-600 font-medium">
                          Approved
                        </span>
                      )}
                      {request.status === 'rejected' && (
                        <span className="text-sm text-red-600 font-medium">
                          Rejected
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
